import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Card } from 'react-bootstrap'
import { DialogContext } from '../../../../../store/context/DialogContext'
import { doGET } from '../../../../../util/HttpUtil'
import ProofOfDocument from './ProofOfDocument'
import { STEP_NUM } from '../../BPlushelper'
import ButtonGroupNew from './ButtonGroupNew'
import useDocTypeRules from '../../../../../hooks/useDocTypeRules'
import { DOC_TYPE_FORM_TYPES, buildDefaultDocTypeRules } from '../../../../../util/docTypeRuleDefaults'
import { DOC_TYPE_SCOPE, splitDocTypesByScope, useDocTypes } from '../../../../../util/docTypeRuleMeta'
import { evaluateDocTypeRules, formatIndividualName } from '../../../../../util/docTypeRuleEvaluator'
import { FaSpinner } from 'react-icons/fa'

const Page5 = ({ loading, onNextClick = () => { }, onBackClick = () => { }, step, isAskForInfo, remark, recordId, asLabel, setHasError, formId, data, handleValidation }) => {
    const [internalLoading, setInternalLoading] = useState(false)
    const { showError } = useContext(DialogContext)
    const [errors, setErrors] = useState([]);
    const { rules: docTypeRules, loading: docRuleLoading, error: docRuleError } = useDocTypeRules({
        formType: DOC_TYPE_FORM_TYPES.BUSINESS_PLUS,
        applicationData: data,
    });
    const { docTypeMap, getMeta, loading: docTypesLoading, error: docTypesError } = useDocTypes();

    useEffect(() => {
        if (docRuleError) {
            showError(docRuleError?.message || "Failed to load document rules.");
        }
    }, [docRuleError, showError]);

    useEffect(() => {
        if (docTypesError) {
            showError(docTypesError?.message || "Failed to load document types.");
        }
    }, [docTypesError, showError]);

    const checkIsDocumentUploaded = async ({ recordId, ubo_id, type }) => {
        try {
            const response = await doGET(`/api/application-doc/grid?rows=-1&form_id=${recordId}${ubo_id ? `&ubo_id=${ubo_id}` : ""}&type=${type}`);
            if (response?.status === 200 && response?.data?.rows[0]?.attachment?.urls?.length) {
                return true;
            }
        } catch (error) {
            showError(error);
        }
        return false;
    };

    const effectiveRules = useMemo(() => {
        if (docTypeRules?.length) return docTypeRules;
        return buildDefaultDocTypeRules().filter((rule) => rule.formType === DOC_TYPE_FORM_TYPES.BUSINESS_PLUS);
    }, [docTypeRules]);

    const applicationDocTypes = useMemo(() => {
        const evaluated = evaluateDocTypeRules(effectiveRules, data) || [];
        return splitDocTypesByScope(evaluated, docTypeMap);
    }, [effectiveRules, data, docTypeMap]);

    const businessDocMetas = useMemo(() => {
        return (applicationDocTypes.business || []).map((docType) =>
            getMeta(docType, { scope: 'business' })
        );
    }, [applicationDocTypes.business, getMeta]);

    const ruleBasedUboRequirements = useMemo(() => {
        if (!effectiveRules?.length) return [];
        const individuals = Array.isArray(data?.individuals) ? data.individuals : [];

        return individuals
            .map((individual) => {
                const evalData = { ...data, individuals: [individual] };
                const matched = evaluateDocTypeRules(effectiveRules, evalData).filter(
                    (docType) => getMeta(docType).scope === DOC_TYPE_SCOPE.INDIVIDUAL,
                );
                return matched.length
                    ? {
                        individual,
                        uboId: individual?._id,
                        displayName: formatIndividualName(individual),
                        docTypes: matched,
                    }
                    : null;
            })
            .filter(Boolean);
    }, [data, effectiveRules, getMeta]);

    const finalUboDocRequirements = ruleBasedUboRequirements;

    const validateBusDocuments = async () => {
        const errors = [];
        let hasAnyError = false;

        for (const doc of businessDocMetas) {
            try {
                const isUploaded = await checkIsDocumentUploaded({ recordId, type: doc.type });
                if (!isUploaded) {
                    hasAnyError = true;
                    errors.push({ type: doc.type, error: doc.errorMessage })
                } else {
                    errors[doc.type] = '';
                }
            } catch (error) {
                hasAnyError = true;
                errors.push({ type: doc.type, error: `Error checking document: ${doc.type}` })
            }

        }

        return { isValid: !hasAnyError, busErrors: errors };
    };

    const handleSubmit = async () => {

        setInternalLoading(true);
        setErrors([]);
        const newErrors = [];

        for (const entry of finalUboDocRequirements) {
            for (const docType of entry.docTypes) {
                const meta = getMeta(docType, { displayName: entry.displayName, individual: entry.individual, scope: 'individual' });
                const isDocumentUploaded = await checkIsDocumentUploaded({ ubo_id: entry?.uboId, type: meta.type, recordId });
                if (!isDocumentUploaded) {
                    newErrors.push({ error: meta.errorMessage, type: docType, ubo_id: entry?.uboId });
                }
            }
        }

        const { isValid, busErrors } = await validateBusDocuments();
        const allErrors = [...(newErrors ?? []), ...(busErrors ?? [])]
        if (allErrors.length > 0 || !isValid) {
            setHasError(true);
            setErrors(allErrors);
            if (allErrors[0]?.error) {
                showError(allErrors[0]?.error);
            }
            setInternalLoading(false);
            return;
        }

        setHasError(false);
        onNextClick();
        setInternalLoading(false);
    };

    const noDocumentsRequired = !businessDocMetas?.length && !finalUboDocRequirements?.length;

    const isDocumentPageReady = !docRuleLoading && !docTypesLoading;

    return (
        <div>
            <h2 className='pt-3'>Document Upload Center</h2>
            <p style={{ fontSize: 12, fontStyle: 'italic' }}>Please upload all of the required documents for your application. If you do not have them ready, you can return later to complete the application.</p>

            <div className='py-2'>Important things to note:</div>
            <ul>
                <li>All documents must be in English or accompanied by an official certified translated copy.</li>
                <li>Register of directors and shareholders must be certified by CPA/Lawyer/Notary.</li>
                <li>Please read the help text below the upload box for each document and its requirements.</li>
            </ul>

            {(docRuleLoading || docTypesLoading) && (
                <div style={{ minHeight: 120 }} className="d-flex align-items-center justify-content-center gap-2">
                    <FaSpinner size={14} className="spinner" />
                    <span className='text-muted small'>Loading document rules…</span>
                </div>
            )}

            {isDocumentPageReady && noDocumentsRequired && (
                <Card className='p-3 mb-3'>
                    <div className='fw-semibold'>No documents required right now.</div>
                    <div className='text-muted small'>We will notify you if verification materials are needed later.</div>
                </Card>
            )}

            {isDocumentPageReady && finalUboDocRequirements?.map((entry) => (
                <Card className='p-3 mb-3' key={entry?.uboId || entry?.displayName}>
                    {entry.docTypes.map((docType) => {
                        const meta = getMeta(docType, { displayName: entry.displayName, individual: entry.individual, scope: 'individual' });
                        return (
                            <ProofOfDocument
                                key={`${entry?.uboId}-${docType}`}
                                dataTestId={`${meta.dataTestId}-${entry?.uboId || 'ubo'}`}
                                validateByAi={meta.validateByAi}
                                isAskForInfo={isAskForInfo}
                                remark={remark}
                                recordId={recordId}
                                asLabel={asLabel}
                                label={meta.label}
                                field={meta.field}
                                showCategory={meta.showCategory}
                                ubo_id={entry?.uboId}
                                type={meta.type}
                                handleValidation={handleValidation}
                                formId={formId}
                                single={meta.single}
                                supportedFiles={meta.supportedFiles}
                                error={errors.find(err => err.ubo_id === entry?.uboId && err.type === docType)?.error}
                            />
                        );
                    })}
                </Card>
            ))}

            {isDocumentPageReady && businessDocMetas.map((docMeta) => (
                <ProofOfDocument
                    key={docMeta.type}
                    dataTestId={docMeta.dataTestId}
                    isAskForInfo={isAskForInfo}
                    remark={remark}
                    recordId={recordId}
                    asLabel={asLabel}
                    label={docMeta.label}
                    field={docMeta.field}
                    showCategory={docMeta.showCategory}
                    type={docMeta.type}
                    single={docMeta.single}
                    validateByAi={docMeta.validateByAi}
                    supportedFiles={docMeta.supportedFiles}
                    error={errors.find(err => err.type === docMeta.type)?.error}
                />
            ))}

            <p className='fs-6 py-2'>
                Once you submit your application, each of the Officers, Authorized Users and Shareholders with 25% or more equity will be sent a text message to verify their idenitiy. Please be sure each person completes the indetity verification within 48 hours or the applicaiton will be placed on hold.
            </p>

            <div className='mt-4'>
                <ButtonGroupNew
                    onNextClick={handleSubmit}
                    onBackClick={onBackClick}
                    showPageNum
                    pageNum={STEP_NUM.DOCUMENTS}
                    isDisabled={asLabel}
                    isReadyForReviewAction={isDocumentPageReady}
                    reviewPendingReason="Please wait while document requirements finish loading."
                    asLabel={asLabel}
                    loading={internalLoading || loading || docRuleLoading || docTypesLoading}
                    step={step}
                />
            </div>
        </div>
    )
}

export default Page5;
